import React from 'react';
import {
  Box,
  Card,
  CardHeader,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Grid,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import { MDTypography } from '../../components/MDTypography';
import { ClaimsBillsServiceLine } from './ClaimDetailsTypes';
import { getPlaceOfServiceDescription } from './placeOfServiceCodes';

const useStyles = makeStyles((theme) => ({
  container: {
    maxWidth: '100%',
    '& .MuiCard-root': {
      marginBottom: theme.spacing(2),
    },
  },
  serviceHeader: {
    backgroundColor: theme.palette.grey[50],
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  table: {
    minWidth: 650,
  },
  tableContainer: {
    maxHeight: 400,
    overflowX: 'auto',
  },
  tableCell: {
    padding: theme.spacing(1),
    fontSize: '0.875rem',
  },
  headerCell: {
    backgroundColor: theme.palette.grey[100],
    fontWeight: 600,
    position: 'sticky',
    top: 0,
    zIndex: 1,
  },
  amountCell: {
    textAlign: 'right',
    fontWeight: 500,
  },
  placeOfServiceChip: {
    fontSize: '0.75rem',
    height: 24,
  },
  noServices: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
  },
}));

interface ServiceLinesViewProps {
  serviceLines: ClaimsBillsServiceLine[];
  billNo: string;
}

export const ServiceLinesView: React.FC<ServiceLinesViewProps> = ({
  serviceLines,
  billNo,
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const formatCurrency = (amount: number | null) => {
    if (amount === null || amount === undefined) return 'N/A';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString();
    } catch {
      return dateString;
    }
  };

  if (!serviceLines || serviceLines.length === 0) {
    return (
      <Card>
        <CardContent className={classes.noServices}>
          <MDTypography variant="h6" color="secondary">
            {t('No service line details available')}
          </MDTypography>
          <MDTypography variant="body2" color="secondary">
            {t('Service line information is not available for this bill.')}
          </MDTypography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader
        className={classes.serviceHeader}
        title={
          <MDTypography variant="h6" fontWeight="medium">
            {t('Service Lines Details')}
          </MDTypography>
        }
        subheader={
          <MDTypography variant="body2" color="text">
            {`${t('Bill')}: ${billNo} | ${serviceLines.length} ${t('service line(s)')}`}
          </MDTypography>
        }
      />
      <CardContent>
        <TableContainer className={classes.tableContainer}>
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell className={`${classes.tableCell} ${classes.headerCell}`}>
                  {t('Line #')}
                </TableCell>
                <TableCell className={`${classes.tableCell} ${classes.headerCell}`}>
                  {t('Service Date')}
                </TableCell>
                <TableCell className={`${classes.tableCell} ${classes.headerCell}`}>
                  {t('CPT Code')}
                </TableCell>
                <TableCell className={`${classes.tableCell} ${classes.headerCell}`}>
                  {t('Description')}
                </TableCell>
                <TableCell className={`${classes.tableCell} ${classes.headerCell} ${classes.amountCell}`}>
                  {t('Billed Amount')}
                </TableCell>
                <TableCell className={`${classes.tableCell} ${classes.headerCell} ${classes.amountCell}`}>
                  {t('Savings')}
                </TableCell>
                <TableCell className={`${classes.tableCell} ${classes.headerCell} ${classes.amountCell}`}>
                  {t('Reductions')}
                </TableCell>
                <TableCell className={`${classes.tableCell} ${classes.headerCell} ${classes.amountCell}`}>
                  {t('Payable')}
                </TableCell>
                <TableCell className={`${classes.tableCell} ${classes.headerCell}`}>
                  {t('Place of Service')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {serviceLines.map((line, index) => (
                <TableRow key={line.id || index}>
                  <TableCell className={classes.tableCell}>
                    {line.lineNumber || index + 1}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Box>
                      <MDTypography variant="caption" display="block">
                        {t('From')}: {formatDate(line.dateOfServiceFrom)}
                      </MDTypography>
                      <MDTypography variant="caption" display="block">
                        {t('To')}: {formatDate(line.dateOfServiceTo)}
                      </MDTypography>
                    </Box>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <MDTypography variant="body2" fontWeight="medium">
                      {line.cptCode || 'N/A'}
                    </MDTypography>
                    {line.cptModifier && (
                      <MDTypography variant="caption" color="text">
                        {t('Modifier')}: {line.cptModifier}
                      </MDTypography>
                    )}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <MDTypography variant="body2">
                      {line.cptCodeDescription || 'N/A'}
                    </MDTypography>
                    {line.serviceUnits && line.serviceUnits > 1 && (
                      <MDTypography variant="caption" color="text">
                        {t('Units')}: {line.serviceUnits}
                      </MDTypography>
                    )}
                  </TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.amountCell}`}>
                    <MDTypography variant="body2" fontWeight="medium">
                      {formatCurrency(line.amountBilled)}
                    </MDTypography>
                  </TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.amountCell}`}>
                    <MDTypography 
                      variant="body2" 
                      color={line.savingsAmount && line.savingsAmount > 0 ? "success" : "text"}
                    >
                      {formatCurrency(line.savingsAmount)}
                    </MDTypography>
                  </TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.amountCell}`}>
                    <MDTypography 
                      variant="body2"
                      color={line.reductions && line.reductions > 0 ? "warning" : "text"}
                    >
                      {formatCurrency(line.reductions)}
                    </MDTypography>
                  </TableCell>
                  <TableCell className={`${classes.tableCell} ${classes.amountCell}`}>
                    <MDTypography variant="body2" fontWeight="medium">
                      {formatCurrency(line.payableAmount)}
                    </MDTypography>
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {line.placeOfServiceCode ? (
                      <Chip
                        label={`${line.placeOfServiceCode}: ${getPlaceOfServiceDescription(line.placeOfServiceCode)}`}
                        size="small"
                        variant="outlined"
                        className={classes.placeOfServiceChip}
                      />
                    ) : (
                      <MDTypography variant="body2" color="secondary">
                        N/A
                      </MDTypography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Summary Row */}
        <Box mt={2}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <MDTypography variant="body2" fontWeight="medium">
                {t('Total Billed')}: {formatCurrency(
                  serviceLines.reduce((sum, line) => sum + (line.amountBilled || 0), 0)
                )}
              </MDTypography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <MDTypography variant="body2" fontWeight="medium" color="success">
                {t('Total Savings')}: {formatCurrency(
                  serviceLines.reduce((sum, line) => sum + (line.savingsAmount || 0), 0)
                )}
              </MDTypography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <MDTypography variant="body2" fontWeight="medium" color="warning">
                {t('Total Reductions')}: {formatCurrency(
                  serviceLines.reduce((sum, line) => sum + (line.reductions || 0), 0)
                )}
              </MDTypography>
            </Grid>
            <Grid item xs={12} sm={3}>
              <MDTypography variant="body2" fontWeight="medium">
                {t('Total Payable')}: {formatCurrency(
                  serviceLines.reduce((sum, line) => sum + (line.payableAmount || 0), 0)
                )}
              </MDTypography>
            </Grid>
          </Grid>
        </Box>
      </CardContent>
    </Card>
  );
};
